import React from 'react'

import { formatSSN } from 'utils'
import { type MembersEnrollment, type Money } from 'utils/web_api.schemas'

/**
 * Determines display configuration for enrollment cards based on enrollment state and timing.
 *
 * This helper analyzes an enrollment's progress state, completion dates, and coverage periods
 * to determine which UI elements should be shown in the enrollment display:
 * - Progress tracker for active enrollments needing attention or recently completed
 * - Financial status when tracker is not shown
 * - Payment responsibility indicators
 *
 * @param enrollment - The member's enrollment object containing coverage dates, progress events, and financial info
 * @param isPriorYear - Whether this enrollment is from a previous year (affects tracker visibility)
 * @returns Object containing display flags and processed dates:
 *   - startDate: Parsed coverage start date or undefined
 *   - endDate: Parsed coverage end date or undefined
 *   - showTracker: Whether to show progress tracking (for current year enrollments needing attention, recently enrolled, or enrolled with future coverage)
 *   - showFinancialStatus: Whether to show financial status (when tracker is hidden and status exists)
 *   - showWhoPays: Whether to show payment responsibility indicator (based on thatch_pays field)
 */
export const enrollmentDisplayHelper = (
  enrollment: MembersEnrollment,
  isPriorYear: boolean
) => {
  const today = new Date()
  const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000)

  const startDate = enrollment.coverage_dates?.coverage_start_date
    ? new Date(enrollment.coverage_dates.coverage_start_date)
    : undefined
  const endDate = enrollment.coverage_dates?.coverage_end_date
    ? new Date(enrollment.coverage_dates.coverage_end_date)
    : undefined

  const progressEvents = enrollment.overview?.progress_events || []
  const completedEnrolledEvent = progressEvents.find(
    (event) => event.label === 'Enrolled' && event.state === 'completed'
  )
  const completedEnrolledDate = completedEnrolledEvent?.completion_date
    ? new Date(completedEnrolledEvent.completion_date)
    : undefined

  const coverageStartsInFuture = startDate && startDate > today

  // Show tracker if enrolled within the last 3 days (gives user time to see confirmation)
  const recentlyEnrolled =
    enrollment.overview?.progress_state === 'enrolled' &&
    completedEnrolledDate &&
    completedEnrolledDate > threeDaysAgo

  // Show tracker if enrolled but coverage hasn't started yet
  const enrolledButNotYetCovered =
    enrollment.overview?.progress_state === 'enrolled' && coverageStartsInFuture

  const showTracker =
    !isPriorYear &&
    (['on_track', 'needs_attention', 'failed'].includes(
      enrollment.overview?.progress_state || ''
    ) ||
      recentlyEnrolled ||
      enrolledButNotYetCovered)
  const showFinancialStatus =
    !showTracker && enrollment.overview?.financial_status
  // Since thatch_pays is nullable, we need to check for undefined and null to determine if we can show the badge
  const showWhoPays =
    enrollment.thatch_pays !== undefined && enrollment.thatch_pays !== null

  return {
    startDate,
    endDate,
    showTracker,
    showFinancialStatus,
    showWhoPays,
  }
}

/**
 * Converts a snake case string to sentence case.
 * @param str - The string to convert
 * @returns The sentence case string
 */
export const snakeCaseToSentenceCase = (str: string) => {
  return str
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/^./, (char) => char.toUpperCase())
}

/**
 * Returns a personalized greeting message based on onboarding completion date and current time.
 *
 * @param name - The user's name for personalization
 * @param onboardingCompletionDate - (Optional) The date when the user completed onboarding
 * @returns A personalized greeting string
 *
 * Logic:
 * - Within 24 hours of completing onboarding: "Welcome to Thatch, {name}!"
 * - 5:00 AM – 12:00 PM (user local time): "Good morning, {name}!"
 * - 12:01 PM – 4:59 PM (user local time): "Good afternoon, {name}!"
 * - 5:00 PM – 9:59 PM (user local time): "Good evening, {name}!"
 * - 10:00 PM – 4:59 AM (user local time): "Welcome back, night owl 🦉"
 * - Fallback (errors or edge cases): "Welcome back, {name}!"
 */
export function getGreeting (
  name: string,
  onboardingCompletionDate?: Date
): string {
  // Guard against undefined, null, or empty name
  const trimmedName = name?.trim()
  const personalization = trimmedName ? `, ${trimmedName}` : ''
  const defaultGreeting = `Welcome back${personalization}!`

  try {
    const now = new Date()

    if (onboardingCompletionDate) {
      const timeDifference = now.getTime() - onboardingCompletionDate.getTime()

      // We are within 24 hours of completing onboarding
      const twentyFourHoursInMs = 24 * 60 * 60 * 1000
      if (timeDifference >= 0 && timeDifference < twentyFourHoursInMs) {
        return `Welcome to Thatch${personalization}!`
      }
    }

    // Get current hour and minutes in user's local time
    const currentHour = now.getHours()
    const currentMinutes = now.getMinutes()

    // Time-based greetings
    if (
      currentHour >= 5 &&
      (currentHour < 12 || (currentHour === 12 && currentMinutes === 0))
    ) {
      // 5a – 12p (5:00 AM to 12:00 PM)
      return `Good morning${personalization}!`
    } else if (
      (currentHour === 12 && currentMinutes > 0) ||
      (currentHour >= 13 && currentHour <= 16)
    ) {
      // 12:01p – 4:59p (12:01 PM to 4:59 PM)
      return `Good afternoon${personalization}!`
    } else if (currentHour >= 17 && currentHour <= 21) {
      // 5 – 9:59p (5:00 PM to 9:59 PM)
      return `Good evening${personalization}!`
    } else if (currentHour >= 22 || currentHour <= 4) {
      // 10p – 4:59a (10:00 PM to 4:59 AM)
      return 'Welcome back, night owl 🦉'
    }

    // Fallback, which should never be reached
    return defaultGreeting
  } catch {
    // Fallback if there's any issue getting local time
    return defaultGreeting
  }
}

/**
 * Returns CSS classes for displaying amount based on whether it's positive or negative.
 * @param money - The money object
 * @returns CSS class string for styling the amount
 */
export const getMoneyClasses = (money: Money) => {
  if (money.subunits > 0) {
    return 'body-bold text-green-400'
  } else {
    return 'body text-main'
  }
}

/**
 * Returns the text for a money object with a "+" prefix for positive amounts.
 * @param money - The money object
 * @returns The text for the money object
 */
export const getMoneyText = (money: Money) => {
  return money.subunits > 0
    ? `+${money.formatted_amount}`
    : money.formatted_amount
}

/**
 * Converts an array of strings into a grammatically correct prose string with proper conjunction.
 * @param items - Array of strings to convert to prose
 * @param conjunction - Word to use before the last item (default: 'and')
 * @param useOxfordComma - Whether to use Oxford comma for lists with 3+ items (default: true)
 * @returns Formatted prose string or empty string if there are no items
 */
export const listToProseString = (
  items: string[],
  conjunction: string = 'and',
  useOxfordComma: boolean = true
) => {
  if (!items || items.length === 0) {
    return ''
  }

  let toReturn = items[0]

  if (items.length > 1) {
    const allButLast = items.slice(0, -1)
    const oxfordComma = useOxfordComma && items.length > 2 ? ',' : ''
    const lastItem = items[items.length - 1]
    toReturn = `${allButLast.join(
      ', '
    )}${oxfordComma} ${conjunction} ${lastItem}`
  }

  return toReturn
}

export const renderFormattedSSN = (ssn: string, showFullSSN: boolean) => {
  if (!ssn) return null

  const formattedSSN = formatSSN(ssn, !showFullSSN, '•')
  const parts = formattedSSN.split('-')

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && <span className='text-gray-300'>—</span>}
        </React.Fragment>
      ))}
    </>
  )
}

export const consideredActive = ({ endDate, enrollment }: { endDate: Date, enrollment: MembersEnrollment }) => {
  const today = new Date()
  const hasCurrentOrFutureCoverage = endDate && endDate >= today
  const rtnValue = hasCurrentOrFutureCoverage && enrollment.overview?.financial_status !== 'inactive'
  return Boolean(rtnValue)
}

/**
 * Sorts enrollments: active by earliest start date, inactive by latest end date, then merges them
 * We always want to show active enrollment first. But if there are multiple active enrollments (for some reason)
 * we want to show an earlier enrollment first to show that there will be another active enrollment coming soon.
 * Otherwise if all are inactive, we want to show the latest inactive enrollment first (and show the earlier ones after).
 */
export const sortEnrollments = (
  enrollments: MembersEnrollment[],
  isPriorYear: boolean
): MembersEnrollment[] => {
  if (!enrollments?.length) return []

  const withData = enrollments.map(enrollment => {
    const { startDate, endDate } = enrollmentDisplayHelper(enrollment, isPriorYear)
    return { enrollment, startDate, endDate, isActive: endDate ? consideredActive({ endDate, enrollment }) : false }
  })

  const active = withData.filter(item => item.isActive)
    .sort((a, b) => (a.startDate?.getTime() || Infinity) - (b.startDate?.getTime() || Infinity))
    .map(item => item.enrollment)

  const inactive = withData.filter(item => !item.isActive)
    .sort((a, b) => (b.endDate?.getTime() || 0) - (a.endDate?.getTime() || 0))
    .map(item => item.enrollment)

  return [...active, ...inactive]
}
